import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Ship, ArrowRight, Ticket, Plus } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { inr } from "@/lib/api";

const STATUS = {
  PAID: { label: "Confirmed", cls: "bg-emerald-100 text-emerald-700" },
  PENDING_PAYMENT: { label: "Payment due", cls: "bg-amber-100 text-amber-700" },
  PAYMENT_PENDING: { label: "Payment due", cls: "bg-amber-100 text-amber-700" },
  PAYMENT_FAILED: { label: "Failed", cls: "bg-red-100 text-red-700" },
};

export default function MyBookings() {
  const [bookings, setBookings] = useState(null);

  useEffect(() => {
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => setBookings([]));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="overline text-accent mb-1">Your trips</p>
            <h1 className="font-display font-bold text-2xl text-primary tracking-tight">My bookings</h1>
          </div>
          <Button className="bg-accent hover:bg-accent/90 text-white" asChild>
            <Link to="/" data-testid="new-booking-btn"><Plus className="h-4 w-4 mr-1.5" /> New booking</Link>
          </Button>
        </div>

        {bookings === null ? (
          <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-24 bg-white rounded-xl border border-border animate-pulse" />)}</div>
        ) : bookings.length === 0 ? (
          <div className="bg-white border border-border rounded-xl p-12 text-center" data-testid="no-bookings">
            <Ticket className="h-10 w-10 text-primary/20 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display font-semibold text-lg text-primary">No bookings yet</h3>
            <p className="text-muted-foreground text-sm mt-1">Search a route and book your first crossing.</p>
            <Button className="mt-5 bg-accent hover:bg-accent/90 text-white" asChild><Link to="/">Find ferries</Link></Button>
          </div>
        ) : (
          <div className="space-y-3">
            {bookings.map((b) => {
              const st = STATUS[b.status] || { label: b.status, cls: "bg-slate-100 text-slate-700" };
              return (
                <div key={b.id} className="bg-white border border-border rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`booking-row-${b.booking_ref}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-muted-foreground">{b.booking_ref}</span>
                      <Badge className={`${st.cls} border-0`} data-testid={`status-${b.booking_ref}`}>{st.label}</Badge>
                    </div>
                    <div className="flex items-center gap-2 font-display font-semibold text-primary">
                      {b.from_port?.city} <ArrowRight className="h-4 w-4 text-accent" /> {b.to_port?.city}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                      <Ship className="h-3 w-3" /> {b.ferry_name} · {b.departure_time} · {b.travel_date && format(new Date(b.travel_date), "dd MMM yyyy")} · <span className="capitalize">{b.travel_class}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 sm:flex-col sm:items-end">
                    <span className="font-display font-bold text-primary">{inr(b.amount)}</span>
                    {b.status === "PAID" ? (
                      <Button variant="outline" size="sm" asChild><Link to={`/confirmation/${b.id}`} data-testid={`view-ticket-${b.booking_ref}`}>View ticket</Link></Button>
                    ) : (
                      <Button size="sm" className="bg-accent hover:bg-accent/90 text-white" asChild><Link to={`/checkout/${b.id}`} data-testid={`pay-now-${b.booking_ref}`}>Pay now</Link></Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
